// TextSection.tsx
import React from 'react';
import { Box, Typography } from '@mui/material';

// 只接受物件型式的 sx，避免前面遇到的 SxProps<Theme> 衝突
type PlainSx = Record<string, any>;

export interface TextSectionProps {
    title?: string;
    subTitle?: string;
    texts?: string | Array<string | null | undefined>;

    containerSx?: PlainSx;
    titleSx?: PlainSx;
    subTitleSx?: PlainSx;
    textSx?: PlainSx;

    /** data-testid（預設 text-area） */
    testId?: string;

    /** 兩段文字間距（預設 0.5rem） */
    gapBetweenTexts?: string;

    /** 最外層第一段上間距（預設 1rem） */
    outerTop?: string;

    /** 最外層最後一段下間距（預設 1rem） */
    outerBottom?: string;
}

const defaultTitleSx: PlainSx = {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '100%',
    letterSpacing: '0em',
};

const defaultSubTitleSx: PlainSx = {
    fontFamily: 'Poppins',
    fontWeight: 500,
    fontSize: '18px',
    lineHeight: '100%',
    letterSpacing: '0em',
    color: '#B75332',
};

const defaultTextSx: PlainSx = {
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '1.3rem',
    letterSpacing: '0em',
};

function toArray(texts?: string | Array<string | null | undefined>): string[] {
    if (!texts) return [];
    const arr = Array.isArray(texts) ? texts : [texts];
    return arr
        .map((t) => (t ?? '').toString())
        .map((t) => t.trim())
        .filter(Boolean);
}

const TextSection: React.FC<TextSectionProps> = ({
    title,
    subTitle,
    texts,
    containerSx,
    titleSx,
    subTitleSx,
    textSx,
    testId = 'text-area',
    gapBetweenTexts = '0.5rem',
    outerTop = '1rem',
    outerBottom = '1rem',
}) => {
    const paragraphs = toArray(texts);

    return (
        <Box data-testid={testId} sx={{ ...(containerSx || {}) }}>
            {title && (
                <Typography sx={{ ...defaultTitleSx, ...(titleSx || {}) }}>{title}</Typography>
            )}

            {subTitle && (
                <Typography sx={{ ...defaultSubTitleSx, ...(subTitleSx || {}) }}>
                    {subTitle}
                </Typography>
            )}

            {paragraphs.map((t, i) => {
                const isFirst = i === 0;
                const isLast = i === paragraphs.length - 1;
                const pt = isFirst ? outerTop : gapBetweenTexts;
                const pb = isLast ? outerBottom : gapBetweenTexts;

                return (
                    <Typography
                        key={i}
                        sx={{
                            ...defaultTextSx,
                            paddingTop: pt,
                            paddingBottom: pb,
                            ...(textSx || {}),
                        }}
                    >
                        {t}
                    </Typography>
                );
            })}
        </Box>
    );
};

export default TextSection;
